import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Lock, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';

export default function Settings() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const isGoogleUser = user?.auth_provider === 'google';
  
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim() || !profileForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setSavingProfile(true);
    try {
      await authAPI.updateProfile(profileForm);
      toast.success(t('profileUpdated'));
      // Update local storage
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, name: profileForm.name, email: profileForm.email }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('All password fields are required');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('passwordsNoMatch'));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success(t('passwordUpdated'));
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error(t('confirmDelete'));
      return;
    }

    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      toast.success(t('accountDeleted'));
      logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('settings')}</h1>
        <p className="text-[#78716C] mt-1">{t('accountSettings')}</p>
      </div>

      {/* Account Info */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <User className="w-5 h-5 text-[#78866B]" />
            {t('accountInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
              <span className="text-[#78716C]">{t('email')}</span>
              <span className="text-[#292524]">{user?.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
              <span className="text-[#78716C]">{t('fullName')}</span>
              <span className="text-[#292524]">{user?.name}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[#78716C]">{t('memberSince')}</span>
              <span className="text-[#292524]">
                {user?.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy') : '-'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Update Profile */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium">{t('updateProfile')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('fullName')}</Label>
              <Input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="h-11"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className="h-11"
                data-testid="profile-email-input"
              />
            </div>
            <Button 
              type="submit" 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
              disabled={savingProfile}
              data-testid="save-profile-btn"
            >
              {savingProfile && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password - Hidden for Google-only users */}
      {isGoogleUser ? (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#78866B]" />
              {t('googleAccountNote')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#78716C]">{t('passwordNotAvailable')}</p>
          </CardContent>
        </Card>
      ) : (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#78866B]" />
            {t('changePassword')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('currentPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="h-11"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('newPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder={t('minPassword')}
                className="h-11"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('confirmPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="h-11"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
              disabled={savingPassword}
              data-testid="change-password-btn"
            >
              {savingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('changePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>
      )}

      {/* Delete Account */}
      <Card className="border-0 shadow-sm border-red-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium text-red-600 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            {t('dangerZone')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#78716C] mb-4">{t('deleteAccountWarning')}</p>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-red-600">{t('confirmDelete')}</Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="h-11 border-red-200 focus:ring-red-500"
                data-testid="delete-confirm-input"
              />
            </div>
            <Button 
              type="button"
              variant="destructive"
              className="rounded-full"
              disabled={deleteConfirm !== 'DELETE' || deleting}
              onClick={handleDeleteAccount}
              data-testid="delete-account-btn"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('deleteAccount')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
